import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { CategoryUpdateInput } from "@/modules/categories/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircleIcon } from "lucide-react";
import {
  useCategoriesQuery,
  useCategoryQuery,
  useUpdateCategory,
} from "../api/queries";
import { CategoryFormTour, useCategoryFormTour } from "../tour";
import { CategoryForm } from "../ui/CategoryForm";

export function CategoryEditPage() {
  const nav = useNavigate({ from: "/app/catalog/categories/$id/edit" });
  const { id } = useParams({ from: "/app/catalog/categories/$id/edit" });
  const { data, ...result } = useCategoryQuery(id);
  const updateCategory = useUpdateCategory(id);
  const categoriesQuery = useCategoriesQuery({ limit: 1000 });
  const categories = categoriesQuery.data?.data ?? [];
  const { run, handleJoyrideCallback, startTour } = useCategoryFormTour();

  const category = data;

  const categoryName =
    category && typeof category.name === "object" && category.name !== null
      ? ((category.name as { lo?: string })?.lo ??
        (category.name as { en?: string })?.en ??
        category.slug)
      : (category?.slug ?? id ?? "ແກ້ໄຂ");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຫມວດມູ່", to: "/app/catalog/categories" },
            { label: categoryName },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂຫມວດມູ່{" "}
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="ml-2 size-6"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircleIcon className="h-4 w-4" />
              </Button>
            </h2>
            <p className="text-muted-foreground">ປັບລາຍລະອຽດຂອງຫມວດມູ່.</p>
          </div>

          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/catalog/categories" } as any)}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດຫມວດມູ່"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!category ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <CategoryForm
                categories={categories}
                initialValues={{
                  nameLo: category.name.lo ?? "",
                  nameTh: category.name.th ?? "",
                  nameEn: category.name.en ?? "",
                  slug: category.slug,
                  parentId: category.parentId,
                  sortOrder: category.sortOrder,
                }}
                onSubmit={async (values: CategoryUpdateInput) => {
                  try {
                    await updateCategory.mutateAsync(values);
                    nav({ to: "/app/catalog/categories" } as any);
                  } catch (error) {
                    // Error handling is done by mutation
                  }
                }}
                submitting={updateCategory.isPending}
              />
            </div>
          )}
        </QueryState>
        <CategoryFormTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
